// src/pages/DashboardPage.js
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';
import { getSubjects, getClassrooms, getCurrentYear, getLevels, getClassroomSettings, getSchoolSettings, getQualityTypes, getQualityProgress, getSchedules, getHolidays, getPeriods, getDashboardStats, getRemedialActivities, getSubstitutes, getClubs, checkMyFeaturePermission } from '../services/api';
import { classroomLabel, levelCodePrefix } from '../utils/classroom';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const ROLE_LABELS = {
  admin: 'ผู้ดูแลระบบ',
  academic: 'ครูวิชาการ',
  assessment: 'ครูวัดผล',
  teacher: 'ครูผู้สอน',
  homeroom: 'ครูประจำชั้น',
};

// ---- helpers เหมือน ClassroomsPage ----
const _GRADE_KEY_DISPLAY = {
  k1:1,k2:2,k3:3,p1:1,p2:2,p3:3,p4:4,p5:5,p6:6,m1:1,m2:2,m3:3,h1:4,h2:5,h3:6,
};
const _ACTIVE_KEYS_MAP = {
  'k1-p6':['k1','k2','k3','p1','p2','p3','p4','p5','p6'],
  'k2-p6':['k2','k3','p1','p2','p3','p4','p5','p6'],
  'k1-m3':['k1','k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'k2-m3':['k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'p1-p6':['p1','p2','p3','p4','p5','p6'],
  'p1-m3':['p1','p2','p3','p4','p5','p6','m1','m2','m3'],
  'm1-m3':['m1','m2','m3'],
  'm1-h3':['m1','m2','m3','h1','h2','h3'],
  'h1-h3':['h1','h2','h3'],
  'p1-h3':['p1','p2','p3','p4','p5','p6','m1','m2','m3','h1','h2','h3'],
  'all':['k1','k2','k3','p1','p2','p3','p4','p5','p6','m1','m2','m3','h1','h2','h3'],
};
function _buildKeyMap(levelList) {
  const kL = levelList.find(l => /อนุบาล|kindergarten/i.test(l.level_name) || /^อ$/i.test(l.level_code));
  const pL = levelList.find(l => /ประถม|primary/i.test(l.level_name) || /^ป$/i.test(l.level_code));
  const mL = levelList.find(l => /ต้น|junior|middle/i.test(l.level_name) || /ม\.ต้น/i.test(l.level_code));
  const hL = levelList.find(l => /ปลาย|senior|high/i.test(l.level_name) || /ม\.ปลาย/i.test(l.level_code));
  const mk = (lvl, g, dg) => lvl ? { level_id: lvl.id, level_code: lvl.level_code, grade: g, displayGrade: dg } : null;
  return {
    k1:mk(kL,1,1),k2:mk(kL,2,2),k3:mk(kL,3,3),
    p1:mk(pL,1,1),p2:mk(pL,2,2),p3:mk(pL,3,3),p4:mk(pL,4,4),p5:mk(pL,5,5),p6:mk(pL,6,6),
    m1:mk(mL,1,1),m2:mk(mL,2,2),m3:mk(mL,3,3),
    h1:mk(hL,4,4),h2:mk(hL,5,5),h3:mk(hL,6,6),
  };
}
function generateDisplayRooms(gradeRange, activeSettings, levelList, dbClassrooms) {
  const gradeKeys = _ACTIVE_KEYS_MAP[gradeRange];
  if (!gradeKeys || activeSettings.length === 0) return [];
  const keyMap = _buildKeyMap(levelList);
  const setting = activeSettings[0];
  const rows = [];
  for (const gk of gradeKeys) {
    const info = keyMap[gk];
    if (!info) continue;
    const roomCount = Number(setting[`rooms_${gk}`] || 0);
    if (roomCount === 0) continue;
    const dg = _GRADE_KEY_DISPLAY[gk];
    for (let room = 1; room <= roomCount; room++) {
      const dbRoom = dbClassrooms.find(
        c => Number(c.level_id) === Number(info.level_id)
          && Number(c.grade) === Number(info.grade)
          && Number(c.room) === room
      );
      rows.push({
        key: `${gk}_${room}`,
        label: `${levelCodePrefix(info.level_code)}${dg}/${room}`,
        level_id: info.level_id, level_code: info.level_code,
        grade: info.grade, displayGrade: dg, room,
        id: dbRoom?.id,
        homeroom1_name: dbRoom?.homeroom1_name,
        homeroom2_name: dbRoom?.homeroom2_name,
        student_count: dbRoom?.student_count || 0,
        homeroom_teacher_id: dbRoom?.homeroom_teacher_id,
        homeroom_teacher2_id: dbRoom?.homeroom_teacher2_id,
        level_name: dbRoom?.level_name || '',
      });
    }
  }
  return rows;
}
// ----------------------------------------

export default function DashboardPage() {
  const { user, isAcademic } = useAuth();
  const navigate = useNavigate();
  const [subjects, setSubjects] = useState([]);
  const [displayRooms, setDisplayRooms] = useState([]);
  const [currentYear, setCurrentYear] = useState(null);
  const [qTypes, setQTypes] = useState([]);
  const [qualProgress, setQualProgress] = useState({});
  const [filterGrade, setFilterGrade] = useState('');
  const [filterRoom, setFilterRoom] = useState('');
  const [isSem2, setIsSem2] = useState(false);
  const [loading, setLoading] = useState(true);
  const [todayLessons, setTodayLessons] = useState([]);
  const [allClubs, setAllClubs] = useState([]);
  const [allSchedules, setAllSchedules] = useState([]);
  const [remedialActivitiesAll, setRemedialActivitiesAll] = useState([]);
  const [adminStats, setAdminStats] = useState(null);
  // ── ตารางสอนทั้งโรงเรียนวันนี้ (สำหรับ admin/academic) ──
  const [schoolDaySchedules, setSchoolDaySchedules] = useState([]);
  const [schoolDaySubstitutes, setSchoolDaySubstitutes] = useState([]);
  const [schoolDayPeriods, setSchoolDayPeriods] = useState([]);
  const [schoolDayInfo, setSchoolDayInfo] = useState({ date: '', dow: 0, isHoliday: false, holidayName: '' });
  const [schoolDayFilterTeacher, setSchoolDayFilterTeacher] = useState('');

  // Helper: determine semester from date + classroom_settings
  const detectSemester = (settings, date) => {
    for (const s of settings) {
      if (s.start_date && s.end_date && date >= s.start_date && date <= s.end_date) return Number(s.team);
    }
    const sorted = [...settings].filter(s => s.start_date).sort((a, b) => b.start_date.localeCompare(a.start_date));
    for (const s of sorted) { if (date >= s.start_date) return Number(s.team); }
    return 1;
  };

  useEffect(() => {
    const fetch = async () => {
      try {
        // ── Batch 1: โหลดข้อมูลพื้นฐานทั้งหมดพร้อมกัน (ลด waterfall) ──
        const [yearRes, subRes, lvlRes, sSettings, clsRes, stRes] = await Promise.all([
          getCurrentYear(),
          getSubjects({ teacher_id: isAcademic() ? undefined : user?.id }),
          getLevels(),
          getSchoolSettings().catch(() => ({ data: { data: null } })),
          getClassrooms({ academic_year_id: '__current__' }).catch(() => ({ data: { data: [] } })),
          getClassroomSettings({ academic_year_id: '__current__' }).catch(() => ({ data: { data: [] } })),
        ]);
        const year = yearRes.data.success ? yearRes.data.data : null;
        if (year) setCurrentYear(year);
        const subList = subRes.data.success ? subRes.data.data : [];
        setSubjects(subList);

        const ss = sSettings.data.data || {};
        const isLockedFn = (v, dt) => (v === 1 || v === '1' || v === true) || (dt && new Date() >= new Date(dt));
        setIsSem2(isLockedFn(ss.lock_sem1, ss.lock_date_sem1));

        const levelList = lvlRes.data.data || [];
        const gradeRange = ss.grade_range || '';

        // ── สร้าง displayRooms จากข้อมูลที่โหลดมาแล้ว (ไม่ต้อง await อีก) ──
        const dbClassrooms = clsRes.data.data || [];
        const activeSettings = (stRes.data.data || []).filter(s => Number(s.is_active) === 1);
        const rooms = generateDisplayRooms(gradeRange, activeSettings, levelList, dbClassrooms);
        setDisplayRooms(rooms.length > 0 ? rooms : dbClassrooms.map(c => ({
          key: `db_${c.id}`, label: classroomLabel(c),
          id: c.id, level_id: c.level_id, level_code: c.level_code,
          grade: c.grade, displayGrade: c.grade, room: c.room,
          homeroom1_name: c.homeroom1_name, homeroom2_name: c.homeroom2_name,
          student_count: c.student_count || 0,
          homeroom_teacher_id: c.homeroom_teacher_id,
          homeroom_teacher2_id: c.homeroom_teacher2_id,
          level_name: c.level_name || '',
        })));

        // ── Batch 2: โหลดข้อมูลเสริม พร้อมกัน (ไม่ block หน้าจอหลัก) ──
        const batch2 = [];

        // Quality types
        if (year) {
          batch2.push(
            getQualityTypes({ academic_year_id: year.id })
              .then(qtRes => {
                const qt = (qtRes.data.data || []).filter(t => Number(t.is_enabled) === 1);
                setQTypes(qt);
              })
              .catch(() => {})
          );
        }

        // Quality progress — 1 request แทนวน loop ทีละห้อง
        if (year && subList.length > 0) {
          const cids = [...new Set(subList.map(s => s.classroom_id).filter(Boolean))];
          if (cids.length > 0) {
            batch2.push((async () => {
              try {
                const r = await getQualityProgress({ classroom_ids: cids.join(','), academic_year_id: year.id });
                if (r.data.success) {
                  const qpMap = {};
                  (r.data.data || []).forEach(d => {
                    if (!qpMap[d.subject_id]) qpMap[d.subject_id] = {};
                    if (!qpMap[d.subject_id][d.type_key]) qpMap[d.subject_id][d.type_key] = {};
                    qpMap[d.subject_id][d.type_key][d.semester] = Number(d.scored_count) || 0;
                  });
                  setQualProgress(qpMap);
                }
              } catch {}
            })());
          }
        }

        // ตารางสอนวันนี้ (สำหรับครู)
        if (year && (user?.role === 'teacher' || user?.role === 'academic')) {
          batch2.push((async () => {
            try {
              const allSemSettings = stRes.data.data || [];
              const today = new Date().toISOString().slice(0, 10);
              const sem = detectSemester(allSemSettings, today);

              const [schRes, holRes, perRes, subAsSubRes, subAsOrigRes, clubsRes] = await Promise.all([
                getSchedules({ academic_year_id: year.id, user_id: user.id, both_semesters: 1 }),
                getHolidays({ academic_year_id: year.id }),
                getPeriods({ academic_year_id: year.id }),
                getSubstitutes({ academic_year_id: year.id, date: today }).catch(() => ({ data: { data: [] } })),
                getSubstitutes({ academic_year_id: year.id, date: today, mode: 'as_original' }).catch(() => ({ data: { data: [] } })),
                getClubs({ academic_year_id: year.id }).catch(() => ({ data: { data: [] } })),
              ]);
              setAllClubs(clubsRes.data?.data || []);
              const schedules = schRes.data.data || [];
              setAllSchedules(schedules);
              // filter: เฉพาะรายการสอนแทนของตัวเอง (backend return ทุกครู)
              const coveredList = (subAsOrigRes.data?.data || []).filter(s =>
                String(s.original_user_id || '') === String(user.id)
              );

              const holidays = holRes.data.data || [];
              const periodsList = perRes.data.data || [];
              const dateObj = new Date(today + 'T00:00:00');
              const rawDow = dateObj.getDay();
              const holidayInfo = holidays.find(h => h.holiday_date === today);
              const isHoliday = holidayInfo?.holiday_type === 'holiday';
              const isCompensation = holidayInfo?.holiday_type === 'compensation';
              const effectiveDow = isCompensation && holidayInfo.compensation_day_of_week
                ? Number(holidayInfo.compensation_day_of_week) : rawDow;
              const isWeekend = rawDow === 0 || rawDow === 6;
              const isSchoolDay = (!isWeekend && !isHoliday) || isCompensation;

              if (isSchoolDay) {
                const lessons = schedules
                  .filter(s => Number(s.day_of_week) === effectiveDow)
                  .map(s => {
                    const isRemNote = s.note?.startsWith('ซ่อมเสริม:');
                    const base = Number(s.is_co_teacher) === 1 ? { ...s, _isCoTeacher: true } : s;
                    const covered = coveredList.find(c =>
                      String(c.classroom_id) === String(s.classroom_id) && String(c.period_id) === String(s.period_id)
                    );
                    const withCover = covered ? { ...base, _coveredBy: covered.substitute_teacher_name, _coveredNote: covered.note } : base;
                    if (!isRemNote) return withCover;
                    const remName = s.note.replace('ซ่อมเสริม:', '').trim();
                    return { ...withCover, subject_name: remName, _isRemedial: true, _remedialColor: '#059669' };
                  })
                  .sort((a, b) => Number(a.period_number) - Number(b.period_number));

                // เพิ่มคาบโฮมรูมสำหรับห้องที่เป็นครูประจำชั้น (ถ้ายังไม่มีใน schedule)
                const myHomeroomCls = dbClassrooms.filter(c =>
                  String(c.homeroom_teacher_id) === String(user.id) || String(c.homeroom_teacher2_id) === String(user.id)
                );

                // ผู้มีสิทธิ์จัดการสอนแทน (หัวหน้าระดับชั้น): เพิ่มห้องของครูประจำชั้นที่ "ไม่อยู่" (มีรายการสอนแทนวันนี้)
                const absentHomeroom = new Map(); // classroom_id → [ชื่อครูที่ไม่อยู่...]
                try {
                  const canManage = !!(await checkMyFeaturePermission('substitute_manage')
                    .then(r => r.data?.allowed).catch(() => false));
                  const allDaySubs = subAsSubRes.data?.data || []; // รายการสอนแทนทั้งหมดของวันนี้ (ทุกครู)
                  if (canManage && allDaySubs.length > 0) {
                    const absentUserIds = new Set(
                      allDaySubs.map(s => String(s.original_user_id || '')).filter(Boolean)
                    );
                    dbClassrooms.forEach(c => {
                      const cid = String(c.id);
                      const hrIds = [String(c.homeroom_teacher_id || ''), String(c.homeroom_teacher2_id || '')].filter(Boolean);
                      hrIds.forEach(hrId => {
                        if (!absentUserIds.has(hrId)) return;
                        const sub = allDaySubs.find(s => String(s.original_user_id) === hrId);
                        const name = sub?.original_teacher_name || '';
                        const list = absentHomeroom.get(cid) || [];
                        if (name && !list.includes(name)) list.push(name);
                        absentHomeroom.set(cid, list);
                      });
                    });
                  }
                } catch { /* ignore */ }

                // รวมห้องที่ต้องแสดงคาบโฮมรูม (ของตัวเอง + ห้องครูที่ไม่อยู่)
                const clsToShow = [...myHomeroomCls];
                absentHomeroom.forEach((_names, cid) => {
                  if (!clsToShow.some(c => String(c.id) === cid)) {
                    const cls = dbClassrooms.find(c => String(c.id) === cid);
                    if (cls) clsToShow.push(cls);
                  }
                });

                clsToShow.forEach(cls => {
                  const hrPeriod = periodsList.find(p =>
                    Number(p.is_homeroom) && (!p.level_id || String(p.level_id) === String(cls.level_id))
                  );
                  if (!hrPeriod) return;
                  const exists = lessons.some(l =>
                    String(l.classroom_id) === String(cls.id) && String(l.period_id) === String(hrPeriod.id)
                  );
                  if (!exists) {
                    lessons.push({
                      classroom_id: cls.id,
                      period_id: hrPeriod.id,
                      period_number: hrPeriod.period_number,
                      period_name: hrPeriod.period_name,
                      start_time: hrPeriod.start_time,
                      end_time: hrPeriod.end_time,
                      is_homeroom: 1,
                      subject_name: 'โฮมรูม',
                      subject_code: null,
                      level_code: cls.level_code,
                      grade: cls.grade,
                      room: cls.room,
                      day_of_week: effectiveDow,
                      // ครูประจำชั้นไม่อยู่ (เราเป็นหัวหน้าระดับมาเช็คแทน)
                      ...(absentHomeroom.has(String(cls.id))
                        ? { _teacherAway: true, _awayTeacherName: (absentHomeroom.get(String(cls.id)) || []).join(', ') }
                        : {}),
                    });
                  }
                });

                // เพิ่มกิจกรรมซ่อมเสริมที่ตรงกับวันนี้ (จาก schedules_raw ที่ยังไม่ได้บันทึกในตารางสอน)
                try {
                  const remRes = await getRemedialActivities({ academic_year_id: year.id });
                  const remActs = remRes.data?.data || [];
                  setRemedialActivitiesAll(remActs);
                  for (const act of remActs) {
                    if (!act.schedules_raw) continue;
                    act.schedules_raw.split(',').forEach(slot => {
                      const [sDow, sPid] = slot.split(':');
                      if (Number(sDow) !== effectiveDow) return;
                      // ไม่เพิ่มซ้ำถ้ามีอยู่ใน lessons แล้ว (note-based)
                      const alreadyIn = lessons.some(l =>
                        l._isRemedial && String(l.classroom_id) === String(act.classroom_id) &&
                        String(l.period_id) === String(sPid)
                      );
                      if (alreadyIn) return;
                      const period = sPid ? periodsList.find(p => String(p.id) === String(sPid)) : null;
                      const key = `remedial_${act.id}_${sPid || '0'}`;
                      if (lessons.some(l => l._key === key)) return;
                      lessons.push({
                        _key: key,
                        classroom_id: act.classroom_id,
                        period_id: period?.id || null,
                        period_number: period?.period_number ?? 99,
                        period_name: period?.period_name ?? 'ซ่อมเสริม',
                        start_time: period?.start_time ?? '',
                        end_time: period?.end_time ?? '',
                        is_homeroom: 0,
                        subject_name: act.name,
                        subject_code: null,
                        level_code: act.level_code || '',
                        grade: act.grade || '',
                        room: act.room || '',
                        day_of_week: effectiveDow,
                        _isRemedial: true,
                        _remedialActivityId: act.id,
                        _remedialColor: act.color || '#059669',
                      });
                    });
                  }
                } catch { /* ignore */ }

                // เพิ่มคาบที่ถูกมอบหมายให้สอนแทน (เฉพาะของตัวเอง)
                try {
                  const subList = (subAsSubRes.data?.data || []).filter(s =>
                    String(s.substitute_user_id || '') === String(user.id)
                  );
                  for (const sub of subList) {
                    const exists = lessons.some(l =>
                      String(l.classroom_id) === String(sub.classroom_id) &&
                      String(l.period_id) === String(sub.period_id)
                    );
                    if (!exists) {
                      const period = periodsList.find(p => String(p.id) === String(sub.period_id));
                      lessons.push({
                        classroom_id: sub.classroom_id,
                        period_id: sub.period_id,
                        period_number: period?.period_number ?? sub.period_number,
                        period_name: period?.period_name ?? sub.period_name,
                        start_time: period?.start_time ?? sub.start_time,
                        end_time: period?.end_time ?? sub.end_time,
                        is_homeroom: period?.is_homeroom ?? 0,
                        subject_name: sub.subject_name || 'สอนแทน',
                        subject_code: sub.subject_code || null,
                        subject_id: sub.effective_subject_id || sub.subject_id || null,
                        level_code: sub.level_name,
                        grade: sub.grade,
                        room: sub.room,
                        day_of_week: effectiveDow,
                        _isSubstitute: true,
                        _substituteFor: sub.original_teacher_name,
                        _substituteNote: sub.note,
                      });
                    }
                  }
                } catch { /* ignore */ }

                lessons.sort((a, b) => Number(a.period_number) - Number(b.period_number));

                // Deduplicate คาบชุมนุม (เพราะมีหลาย row จาก is_co_teacher) + annotate grade_filter
                // และ deduplicate วิชาที่มี co-teacher row (same classroom_id + period_id → เก็บ own row)
                const isClubCheck = (pn, sn) => /ชุมนุม|กิจกรรมชุมนุม|club/i.test(pn || '') || /ชุมนุม|กิจกรรมชุมนุม|club/i.test(sn || '');
                const clubsData = clubsRes.data?.data || [];
                const seenClubPid = new Set();
                // Dedup by classroom_id + period_id: own row (is_co_teacher=0) ชนะ
                const slotMap = new Map();
                for (const l of lessons) {
                  const k = `${l.classroom_id}|${l.period_id}`;
                  const existing2 = slotMap.get(k);
                  if (!existing2 || Number(l.is_co_teacher) === 0) {
                    slotMap.set(k, l);
                  }
                }
                const dedupLessons = [...slotMap.values()].sort((a, b) => Number(a.period_number) - Number(b.period_number));
                const finalLessons = [];
                for (const l of dedupLessons) {
                  if (isClubCheck(l.period_name, l.subject_name)) {
                    if (seenClubPid.has(String(l.period_id))) continue;
                    seenClubPid.add(String(l.period_id));
                    const myClub = clubsData.find(c => String(c.teacher_user_id) === String(user.id));
                    const gradeLabel = myClub?.grade_filter
                      ? myClub.grade_filter.split(',').filter(Boolean).map(gk => {
                          const [lid, grade] = gk.split(':');
                          const cls2 = dbClassrooms.find(c => String(c.level_id) === String(lid));
                          return `${levelCodePrefix(cls2?.level_code || '')}${grade}`;
                        }).join(', ')
                      : 'ทุกชั้น';
                    finalLessons.push({ ...l, _isClub: true, _clubId: myClub?.id, _clubName: myClub?.club_name || '', _clubGradeLabel: gradeLabel });
                  } else {
                    finalLessons.push(l);
                  }
                }
                finalLessons.sort((a, b) => Number(a.period_number) - Number(b.period_number));
                setTodayLessons(finalLessons);
              }
            } catch { /* ignore */ }
          })());
        }

        // รอ batch 2 ทั้งหมด (แต่ไม่ block UI เพราะ setLoading(false) ข้างล่าง)
        // Admin stats
        if (isAcademic()) {
          batch2.push(
            getDashboardStats()
              .then(r => {
                console.log('[Dashboard] getDashboardStats response:', r.data);
                if (r.data.success) setAdminStats(r.data.data);
              })
              .catch(e => { console.error('[Dashboard] getDashboardStats error:', e?.response?.status, e?.response?.data || e?.message); })
          );
        }

        // ── ตารางสอนทั้งโรงเรียนวันนี้ (admin/academic) ──
        if (year && isAcademic()) {
          batch2.push((async () => {
            try {
              const today = new Date().toISOString().slice(0, 10);
              const allSemSettings = stRes.data.data || [];
              const sem = detectSemester(allSemSettings, today);
              const [holRes2, perRes2] = await Promise.all([
                getHolidays({ academic_year_id: year.id }).catch(() => ({ data: { data: [] } })),
                getPeriods({ academic_year_id: year.id }).catch(() => ({ data: { data: [] } })),
              ]);
              const holidays2 = holRes2.data.data || [];
              const periodsList2 = perRes2.data.data || [];
              const dateObj = new Date(today + 'T00:00:00');
              const rawDow = dateObj.getDay();
              const hInfo = holidays2.find(h => h.holiday_date === today);
              const isHoliday2 = hInfo?.holiday_type === 'holiday';
              const isComp = hInfo?.holiday_type === 'compensation';
              const effDow = isComp && hInfo.compensation_day_of_week
                ? Number(hInfo.compensation_day_of_week)
                : rawDow;
              setSchoolDayInfo({
                date: today, dow: effDow, isHoliday: isHoliday2,
                holidayName: hInfo?.holiday_name || '',
              });
              setSchoolDayPeriods(periodsList2);
              if (isHoliday2) { setSchoolDaySchedules([]); setSchoolDaySubstitutes([]); return; }
              const [schAllRes, subAllRes] = await Promise.all([
                getSchedules({ academic_year_id: year.id, semester: sem, day_of_week: effDow }).catch(() => ({ data: { data: [] } })),
                getSubstitutes({ academic_year_id: year.id, date: today }).catch(() => ({ data: { data: [] } })),
              ]);
              setSchoolDaySchedules(schAllRes.data?.data || []);
              setSchoolDaySubstitutes(subAllRes.data?.data || []);
            } catch (e) { console.error('[Dashboard] day schedule error:', e); }
          })());
        }

        await Promise.all(batch2);
      } catch (e) {}
      finally { setLoading(false); }
    };
    if (user) fetch();
  }, [user, isAcademic]);

  if (loading) return <div className="loading-screen"><div className="spinner"/><span>กำลังโหลด...</span></div>;

  const currentYearSubjects = subjects.filter(s => currentYear && String(s.academic_year_id) === String(currentYear.id));

  // รายวิชาของครูคนนี้: ถ้าเป็น academic/admin เห็นทั้งหมด, teacher/homeroom เห็นเฉพาะที่รับผิดชอบ (API กรองมาให้แล้ว)
  // เรียงตาม ระดับชั้น → ห้อง → ประเภทวิชา
  const typeOrder = { 'พื้นฐาน': 1, 'เพิ่มเติม': 2, 'กิจกรรม': 3 };
  const sortedSubjects = [...currentYearSubjects].sort((a, b) => {
    const ga = Number(a.grade) || 0, gb = Number(b.grade) || 0;
    if (ga !== gb) return ga - gb;
    const ra = Number(a.classroom_room) || 0, rb = Number(b.classroom_room) || 0;
    if (ra !== rb) return ra - rb;
    const ta = typeOrder[a.subject_type] || 9, tb = typeOrder[b.subject_type] || 9;
    if (ta !== tb) return ta - tb;
    return (Number(a.sort_order) || 0) - (Number(b.sort_order) || 0);
  });

  // กรอง
  const mySubjects = sortedSubjects.filter(s => {
    if (filterGrade && String(s.grade) !== filterGrade) return false;
    if (filterRoom && String(s.classroom_room) !== filterRoom) return false;
    return true;
  });

  // สร้าง list ชั้น/ห้อง สำหรับ dropdown
  const gradeOptions = [...new Set(currentYearSubjects.map(s => s.grade).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
  const roomOptions = [...new Set(
    currentYearSubjects
      .filter(s => !filterGrade || String(s.grade) === filterGrade)
      .map(s => s.classroom_room)
      .filter(Boolean)
  )].sort((a, b) => Number(a) - Number(b));

  // ห้องเรียน: academic/admin เห็นทั้งหมด, teacher/homeroom เห็นเฉพาะห้องที่ตัวเองเป็นครูประจำชั้น
  const myClasses = isAcademic()
    ? displayRooms
    : displayRooms.filter(c =>
        String(c.homeroom_teacher_id) === String(user?.id) ||
        String(c.homeroom_teacher2_id) === String(user?.id)
      );

  return (
    <div>
      {/* Welcome Banner */}
      <div style={{
        background: 'linear-gradient(135deg, #1565C0, #42a5f5)',
        borderRadius: '16px',
        padding: '28px 32px',
        color: 'white',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: '700', marginBottom: '4px' }}>
            👋 สวัสดี {user?.prefix}{user?.firstname} {user?.lastname}
          </h1>
          <p style={{ opacity: 0.9, fontSize: '15px' }}>
            {ROLE_LABELS[user?.role]} | ปีการศึกษา {currentYear?.year || '...'}
          </p>
        </div>
        <div style={{ fontSize: '48px' }}>
          <svg width="44" height="44" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/>
          </svg>
        </div>
      </div>

      {/* Stats */}
      {(() => {
        // นับรายวิชาทั้งหมด: สำหรับครู (teacher role) ให้นับจาก schedule ทั้งหมด
        // รวมคาบปกติ + คาบร่วม (is_co_teacher) + ซ่อมเสริม (remedial activities)
        const totalSubjectCount = (() => {
          if (isAcademic()) return mySubjects.length;
          // นับ unique (subject_id, classroom_id) จาก allSchedules
          const seen = new Set();
          allSchedules.forEach(s => {
            const key = `${s.subject_id || 'null'}_${s.classroom_id || 'null'}`;
            seen.add(key);
          });
          // นับ remedial activities แยก
          const remCount = remedialActivitiesAll.length;
          return seen.size + remCount;
        })();

        const stats = [
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#1565C0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>,
            label: 'รายวิชาทั้งหมด', value: totalSubjectCount, color: '#1565C0', bg: '#e3f2fd'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
            label: 'ห้องเรียน', value: isAcademic() ? displayRooms.length : myClasses.length, color: '#2e7d32', bg: '#e8f5e9'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#e65100" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>,
            label: 'ปีการศึกษา', value: currentYear?.year || '-', color: '#e65100', bg: '#fff3e0'
          },
          {
            icon: <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6a1b9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>,
            label: 'สถานะ', value: 'ปกติ', color: '#6a1b9a', bg: '#f3e5f5'
          },
        ];
        return (
          <div className="grid-4" style={{ marginBottom: '24px' }}>
            {stats.map((stat, i) => (
              <div key={i} className="card" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                <div style={{ width: '52px', height: '52px', background: stat.bg, borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {stat.icon}
                </div>
                <div>
                  <div style={{ fontSize: '22px', fontWeight: '700', color: stat.color }}>{stat.value}</div>
                  <div style={{ fontSize: '13px', color: '#888' }}>{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        );
      })()}

      {/* Today's Teaching Schedule (Teacher only) */}
      {user?.role === 'teacher' && (
        <div className="card" style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
                <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
              </svg>
              คาบสอนวันนี้
              {todayLessons.length > 0 && <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: 8 }}>({todayLessons.length} คาบ)</span>}
            </h2>
            <button className="btn btn-outline btn-sm" style={{ fontSize: 12, padding: '4px 10px', display: 'flex', alignItems: 'center', gap: 4 }}
              onClick={() => navigate('/attendance')}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              เช็คชื่อรายคาบ
            </button>
          </div>

          {todayLessons.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 16px' }}>
              <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="#bbb" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 10 }}>
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/><path d="M9 16l2 2 4-4"/>
              </svg>
              <div style={{ fontSize: 14, color: '#999', marginBottom: 12 }}>ไม่มีคาบสอนวันนี้</div>
              <button
                onClick={() => navigate('/attendance')}
                style={{
                  padding: '10px 24px', borderRadius: 10, border: 'none',
                  background: 'linear-gradient(135deg, #1976d2, #1565c0)',
                  color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  boxShadow: '0 2px 8px rgba(25,118,210,0.25)',
                }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                ไปหน้าเช็คชื่อรายคาบ
              </button>
            </div>
          ) : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10 }}>
            {todayLessons.map((lesson, i) => {
              const isHomeroom = Number(lesson.is_homeroom) === 1;
              const isClubPeriod = (pn, sn) => {
                const check = (s) => s && /ชุมนุม|กิจกรรมชุมนุม|club/i.test(s);
                return check(pn) || check(sn);
              };
              const handleLessonClick = () => {
                if (isClubPeriod(lesson.period_name, lesson.subject_name)) {
                  const today = new Date().toISOString().slice(0, 10);
                  const myClubs = allClubs.filter(c => String(c.teacher_user_id) === String(user?.id));
                  const pidParam = lesson.period_id ? `&period_id=${lesson.period_id}` : '';
                  if (myClubs.length === 1) {
                    navigate(`/club-attendance?club_id=${myClubs[0].id}&date=${today}${pidParam}`);
                  } else {
                    navigate(`/club-attendance?date=${today}${pidParam}`);
                  }
                  return;
                }
                navigate(`/attendance?classroom_id=${lesson.classroom_id}&period_id=${lesson.period_id}`);
              };
              return (
                <div key={i}
                  onClick={handleLessonClick}
                  style={{
                    padding: '12px 14px', borderRadius: 12,
                    border: lesson._teacherAway ? '2px dashed #e65100'
                      : isHomeroom ? '1.5px solid #c8e6c9' : '1.5px solid #e0e0e0',
                    background: lesson._teacherAway ? 'linear-gradient(135deg, #fff3e0, #ffe0b2)'
                      : isHomeroom ? 'linear-gradient(135deg, #e8f5e9, #f1f8e9)' : '#fff',
                    cursor: 'pointer', transition: 'all 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 3px 12px rgba(0,0,0,0.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = 'none'; }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                    <span style={{
                      fontSize: 13, fontWeight: 700, color: isHomeroom ? '#2e7d32' : '#1565c0',
                      display: 'flex', alignItems: 'center', gap: 4,
                    }}>
                      {isHomeroom ? (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#43a047" stroke="#43a047" strokeWidth="2"><path d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-4 0h4"/></svg>
                      ) : (
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg>
                      )}
                      {lesson.period_name || `คาบ ${lesson.period_number}`}
                    </span>
                    <span style={{ fontSize: 11, color: '#999' }}>
                      {lesson.start_time?.slice(0, 5)}-{lesson.end_time?.slice(0, 5)}
                    </span>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {lesson.subject_name || 'โฮมรูม'}
                  </div>
                  {lesson._isClub && lesson._clubName && (
                    <div style={{ fontSize: 12, color: '#7c3aed', fontWeight: 600, marginBottom: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      🎭 {lesson._clubName}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: lesson._isClub ? '#7c3aed' : '#666', display: 'flex', alignItems: 'center', gap: 4 }}>
                    {lesson._isClub ? (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                        รับชั้น: {lesson._clubGradeLabel || 'ทุกชั้น'}
                      </>
                    ) : (
                      <>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                        {levelCodePrefix(lesson.level_code)}{lesson.grade}/{lesson.room}
                      </>
                    )}
                  </div>
                  {lesson._isSubstitute && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#e65100', background: '#fff3e0', borderRadius: 4, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                      🔄 สอนแทน {lesson._substituteFor}
                    </div>
                  )}
                  {lesson._teacherAway && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#e65100', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 3 }}>
                      🚪 ครูไม่อยู่{lesson._awayTeacherName ? ` (${lesson._awayTeacherName})` : ''} — เช็คชื่อแทน
                    </div>
                  )}
                  {lesson._coveredBy && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#6a1b9a', background: '#f3e5f5', borderRadius: 4, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                      🟡 มีคนสอนแทน: {lesson._coveredBy}
                    </div>
                  )}
                  {lesson._isCoTeacher && !lesson._isClub && (
                    <div style={{ marginTop: 4, fontSize: 10, color: '#7b1fa2', background: '#f3e5f5', borderRadius: 4, padding: '1px 6px', display: 'inline-flex', alignItems: 'center', gap: 3, fontWeight: 600 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#7b1fa2" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                      ครูผู้สอนร่วม
                    </div>
                  )}
                  <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span
                      onClick={(e) => { e.stopPropagation(); handleLessonClick(); }}
                      style={{
                        fontSize: 11, fontWeight: 600, color: '#1565c0', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      เช็คชื่อ
                    </span>
                    <span style={{ color: '#ddd' }}>|</span>
                    <span
                      onClick={(e) => { e.stopPropagation(); if (lesson._isClub) { const now2 = new Date(); navigate(`/club-attendance-report?club_id=${lesson._clubId}&month=${now2.getMonth()+1}&year=${now2.getFullYear()}`); } else { navigate(`/attendance-report?classroom_id=${lesson.classroom_id}&subject_id=${lesson.subject_id || ''}`); } }}
                      style={{
                        fontSize: 11, fontWeight: 600, color: '#43a047', display: 'flex', alignItems: 'center', gap: 3, cursor: 'pointer',
                      }}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#43a047" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                      รายงาน
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
          )}
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          ADMIN / ACADEMIC DASHBOARD
          ══════════════════════════════════════════════════════════ */}
      {isAcademic() && (() => {
        const st = adminStats;

        // ── KPI Cards ──────────────────────────────────────────────
        const kpis = [
          {
            label: 'นักเรียนทั้งหมด', value: st?.total_students ?? '...', sub: (st?.students_by_level||[]).map(l=>`${l.level} ${l.count} คน`).join(' | ') || '',
            color: '#1565c0', bg: '#e3f2fd',
            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>,
          },
          {
            label: 'ครู/บุคลากร', value: st?.teacher_count ?? '...', sub: `บุคลากรทั้งหมด ${st?.staff_count ?? '...'} คน`,
            color: '#2e7d32', bg: '#e8f5e9',
            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
          },
          {
            label: 'เช็คชื่อวันนี้',
            value: st?.attendance_today?.rate != null ? `${st.attendance_today.rate}%` : (st?.attendance_today?.total === 0 ? 'ยังไม่มีข้อมูล' : '...'),
            sub: st?.attendance_today?.total > 0
              ? `มา ${st.attendance_today.present} | ขาด ${st.attendance_today.absent} | สาย ${st.attendance_today.late} | ลา ${st.attendance_today.leave}`
              : 'ยังไม่มีการเช็คชื่อวันนี้',
            color: '#6a1b9a', bg: '#f3e5f5',
            icon: <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#6a1b9a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/><polyline points="22 4 12 14.01 9 11.01"/></svg>,
          },
        ];

        // ── attendance trend bar chart (SVG) ──────────────────────
        const trend = st?.absence_trend || [];
        const maxAbsent = Math.max(...trend.map(t=>t.absent), 1);

        return (
          <>
            {/* KPI */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))', gap:14, marginBottom:20 }}>
              {kpis.map((k,i) => (
                <div key={i} className="card" style={{ display:'flex', alignItems:'flex-start', gap:14 }}>
                  <div style={{ width:52, height:52, background:k.bg, borderRadius:12, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                    {k.icon}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:26, fontWeight:700, color:k.color, lineHeight:1.1 }}>{k.value}</div>
                    <div style={{ fontSize:13, fontWeight:600, color:'#333', marginTop:1 }}>{k.label}</div>
                    <div style={{ fontSize:11, color:'#888', marginTop:2, lineHeight:1.4 }}>{k.sub}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Row: แนวโน้มขาดเรียน + ปัญหาผลการเรียน */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
              {/* แนวโน้มการขาดเรียน 7 วัน */}
              <div className="card">
                <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/></svg>
                  แนวโน้มการขาดเรียน 7 วัน
                </h3>
                {trend.length === 0 ? (
                  <div style={{ textAlign:'center', color:'#bbb', fontSize:13, padding:'20px 0' }}>ยังไม่มีข้อมูล</div>
                ) : (
                  <div style={{ display:'flex', alignItems:'flex-end', gap:8, height:100 }}>
                    {trend.map((t,i) => {
                      const h = maxAbsent > 0 ? Math.round((t.absent/maxAbsent)*80) : 0;
                      const isToday = t.date === new Date().toISOString().slice(0,10);
                      return (
                        <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:4 }}>
                          <div style={{ fontSize:10, fontWeight:700, color: t.absent>0?'#e53935':'#bbb' }}>{t.absent||''}</div>
                          <div style={{ width:'100%', background: isToday?'#e53935':'#ef9a9a', borderRadius:'4px 4px 0 0', height: h||3, minHeight:3, transition:'height 0.3s' }}/>
                          <div style={{ fontSize:10, color: isToday?'#e53935':'#888', fontWeight: isToday?700:400 }}>{t.label}</div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* ผลการเรียนมีปัญหา */}
              <div className="card">
                <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f57c00" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  นักเรียนผลการเรียนมีปัญหา
                </h3>
                {(() => {
                  const p = st?.problem_students || {zero:0,r:0,ms:0,mp:0};
                  const total = p.zero+p.r+p.ms+p.mp;
                  if (total === 0) return <div style={{ textAlign:'center', color:'#bbb', fontSize:13, padding:'20px 0' }}>ไม่พบนักเรียนที่มีปัญหา</div>;
                  const items = [
                    { label:'ผลการเรียน 0', count:p.zero, color:'#c62828', bg:'#ffebee' },
                    { label:'ผลการเรียน ร', count:p.r,    color:'#e65100', bg:'#fff3e0' },
                    { label:'ผลการเรียน มส', count:p.ms,  color:'#f57c00', bg:'#fff8e1' },
                    { label:'ผลการเรียน มผ', count:p.mp,  color:'#6a1b9a', bg:'#f3e5f5' },
                  ].filter(x=>x.count>0);
                  return (
                    <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                      {items.map((item,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:10 }}>
                          <div style={{ width:32, height:32, background:item.bg, borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:13, fontWeight:700, color:item.color }}>{item.count}</div>
                          <div>
                            <div style={{ fontSize:13, fontWeight:600, color:'#333' }}>{item.label}</div>
                            <div style={{ fontSize:11, color:'#888' }}>{item.count} คน</div>
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
            </div>

            {/* Row: ความคืบหน้าการกรอกคะแนน + ห้องขาดเรียนมาก */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14, marginBottom:20 }}>
              {/* ความคืบหน้าคะแนน */}
              <div className="card">
                <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1565c0" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>
                  ความคืบหน้าการกรอกคะแนน
                </h3>
                {(() => {
                  const sp = st?.score_progress || {filled:0,total:0,percent:0};
                  return (
                    <>
                      <div style={{ display:'flex', justifyContent:'space-between', marginBottom:6, fontSize:13 }}>
                        <span style={{ color:'#555' }}>กรอกแล้ว <strong style={{ color:'#1565c0' }}>{sp.filled}</strong> / {sp.total} วิชา</span>
                        <span style={{ fontWeight:700, color: sp.percent>=80?'#2e7d32':sp.percent>=50?'#f57c00':'#c62828' }}>{sp.percent}%</span>
                      </div>
                      <div style={{ height:10, background:'#e3f2fd', borderRadius:6, overflow:'hidden', marginBottom:14 }}>
                        <div style={{ height:'100%', width:`${sp.percent}%`, background: sp.percent>=80?'#43a047':sp.percent>=50?'#ff9800':'#e53935', borderRadius:6, transition:'width 0.5s' }}/>
                      </div>
                      <h4 style={{ fontSize:12, fontWeight:600, color:'#888', margin:'0 0 8px 0' }}>ครูที่ยังไม่กรอกครบ</h4>
                      {(st?.teachers_no_score||[]).length === 0 ? (
                        <div style={{ fontSize:12, color:'#bbb' }}>ทุกคนกรอกครบแล้ว 🎉</div>
                      ) : (st?.teachers_no_score||[]).slice(0,5).map((t,i) => (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6 }}>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ fontSize:12, fontWeight:600, color:'#333', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{t.name}</div>
                            <div style={{ height:4, background:'#eee', borderRadius:3, marginTop:3, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${t.percent}%`, background:'#1565c0', borderRadius:3 }}/>
                            </div>
                          </div>
                          <div style={{ fontSize:11, color:'#e53935', fontWeight:700, whiteSpace:'nowrap' }}>ขาด {t.missing}</div>
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>

              {/* ห้องที่ขาดเรียนมาก */}
              <div className="card">
                <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#e53935" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/></svg>
                  ห้องขาดเรียนมากสุด (สัปดาห์นี้)
                </h3>
                {(st?.top_absent_classes||[]).length === 0 ? (
                  <div style={{ textAlign:'center', color:'#bbb', fontSize:13, padding:'20px 0' }}>ไม่มีข้อมูลสัปดาห์นี้</div>
                ) : (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    {(st?.top_absent_classes||[]).map((cls,i) => {
                      const maxC = (st?.top_absent_classes||[])[0]?.count || 1;
                      const pct = Math.round(cls.count/maxC*100);
                      const medal = i===0?'🥇':i===1?'🥈':i===2?'🥉':'';
                      return (
                        <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{ width:28, fontSize:13, textAlign:'center' }}>{medal||<span style={{ color:'#aaa' }}>#{i+1}</span>}</div>
                          <div style={{ flex:1, minWidth:0 }}>
                            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
                              <span style={{ fontSize:13, fontWeight:600 }}>{cls.class}</span>
                              <span style={{ fontSize:12, color:'#e53935', fontWeight:700 }}>{cls.count} ครั้ง</span>
                            </div>
                            <div style={{ height:4, background:'#ffebee', borderRadius:3, overflow:'hidden' }}>
                              <div style={{ height:'100%', width:`${pct}%`, background:'#e53935', borderRadius:3 }}/>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* นักเรียนขาดเรียนบ่อย */}
            {(st?.top_absent_students||[]).length > 0 && (
              <div className="card" style={{ marginBottom:20 }}>
                <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0', display:'flex', alignItems:'center', gap:6 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c62828" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="23" y1="11" x2="17" y2="11"/></svg>
                  นักเรียนขาดเรียนบ่อย (Top 10 ปีการศึกษานี้)
                </h3>
                <div style={{ overflowX:'auto' }}>
                  <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
                    <thead>
                      <tr style={{ background:'#fff5f5' }}>
                        <th style={{ padding:'8px 10px', textAlign:'left', fontWeight:600, color:'#555', borderBottom:'2px solid #ffcdd2' }}>#</th>
                        <th style={{ padding:'8px 10px', textAlign:'left', fontWeight:600, color:'#555', borderBottom:'2px solid #ffcdd2' }}>ชื่อ-นามสกุล</th>
                        <th style={{ padding:'8px 10px', textAlign:'left', fontWeight:600, color:'#555', borderBottom:'2px solid #ffcdd2' }}>ห้อง</th>
                        <th style={{ padding:'8px 10px', textAlign:'right', fontWeight:600, color:'#c62828', borderBottom:'2px solid #ffcdd2' }}>จำนวนวันขาด</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(st?.top_absent_students||[]).map((s,i) => (
                        <tr key={i} style={{ borderBottom:'1px solid #fce4e4', background: i%2===0?'#fff':'#fff9f9' }}>
                          <td style={{ padding:'7px 10px', color:'#aaa', fontWeight:600 }}>{i+1}</td>
                          <td style={{ padding:'7px 10px', fontWeight:600, color:'#333' }}>{s.name}</td>
                          <td style={{ padding:'7px 10px', color:'#666' }}>{s.class}</td>
                          <td style={{ padding:'7px 10px', textAlign:'right' }}>
                            <span style={{ background:'#ffebee', color:'#c62828', fontWeight:700, padding:'2px 10px', borderRadius:10, fontSize:12 }}>
                              {s.absent_days} วัน
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Quick links สำหรับ admin */}
            <div className="card" style={{ marginBottom:20 }}>
              <h3 style={{ fontSize:14, fontWeight:700, margin:'0 0 14px 0' }}>เมนูด่วน</h3>
              <div style={{ display:'flex', gap:10, flexWrap:'wrap' }}>
                {[
                  { label:'รายวิชา', icon:'📚', path:'/subjects' },
                  { label:'กรอกคะแนน', icon:'✏️', path:'/score-classrooms' },
                  { label:'ตารางสอน', icon:'📅', path:'/attendance' },
                  { label:'จัดการนักเรียน', icon:'👥', path:'/students' },
                  { label:'ครู/บุคลากร', icon:'👨‍🏫', path:'/teachers' },
                  { label:'ชุมนุม', icon:'🏫', path:'/clubs' },
                  { label:'รายงาน', icon:'📊', path:'/evaluation-report' },
                  { label:'ตั้งค่าสถิติ', icon:'⚙️', path:'/settings' },
                ].map((m,i) => (
                  <button key={i}
                    onClick={() => navigate(m.path)}
                    style={{
                      display:'flex', alignItems:'center', gap:6,
                      padding:'8px 16px', borderRadius:10, border:'1.5px solid #e0e0e0',
                      background:'#fff', cursor:'pointer', fontSize:13, fontWeight:600, color:'#333',
                      transition:'all 0.15s',
                    }}
                    onMouseEnter={e=>{ e.currentTarget.style.background='#e3f2fd'; e.currentTarget.style.borderColor='#90caf9'; }}
                    onMouseLeave={e=>{ e.currentTarget.style.background='#fff'; e.currentTarget.style.borderColor='#e0e0e0'; }}
                  >
                    <span>{m.icon}</span>{m.label}
                  </button>
                ))}
              </div>
            </div>
          </>
        );
      })()}

      {/* My Subjects */}
      {!isAcademic() && (mySubjects.length > 0) && (user?.role !== 'homeroom') && (        <div className="card" style={{ marginBottom: '20px' }}>
          <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
            <h2 className="page-title" style={{ margin: 0 }}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
                <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/>
              </svg>
              รายวิชาที่รับผิดชอบ
              <span style={{ fontSize: '13px', fontWeight: 400, color: '#999', marginLeft: 8 }}>({mySubjects.length} วิชา)</span>
            </h2>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
              <select value={filterGrade} onChange={e => { setFilterGrade(e.target.value); setFilterRoom(''); }}
                style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #ddd', fontSize: '13px', background: '#fff', cursor: 'pointer', minWidth: 100 }}>
                <option value="">ทุกชั้น</option>
                {gradeOptions.map(g => <option key={g} value={g}>ป.{g}</option>)}
              </select>
              <select value={filterRoom} onChange={e => setFilterRoom(e.target.value)}
                style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #ddd', fontSize: '13px', background: '#fff', cursor: 'pointer', minWidth: 100 }}>
                <option value="">ทุกห้อง</option>
                {roomOptions.map(r => <option key={r} value={r}>ห้อง {r}</option>)}
              </select>
              {(filterGrade || filterRoom) && (
                <button onClick={() => { setFilterGrade(''); setFilterRoom(''); }}
                  style={{ padding: '5px 10px', borderRadius: '8px', border: '1.5px solid #e0e0e0', background: '#f5f5f5', fontSize: '12px', cursor: 'pointer', color: '#666' }}>
                  ✕ ล้างตัวกรอง
                </button>
              )}
            </div>
          </div>
          {mySubjects.length === 0 ? (
            <div className="text-center text-muted" style={{ padding: '40px' }}>ไม่มีรายวิชาในขณะนี้</div>
          ) : (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '14px' }}>
              {mySubjects.map(sub => (
                <div key={sub.id} style={{
                  border: '1.5px solid #e8e8e8',
                  borderRadius: '12px',
                  padding: '16px',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  cursor: 'pointer',
                }}
                  onMouseEnter={e => e.currentTarget.style.borderColor = '#2196F3'}
                  onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '8px' }}>
                    <span style={{ fontWeight: '700', fontSize: '15px', color: '#1565C0' }}>{sub.subject_code}</span>
                    <span className={`badge badge-${sub.subject_type === 'พื้นฐาน' ? 'primary' : 'warning'}`}>
                      {sub.subject_type}
                    </span>
                  </div>
                  <p style={{ fontWeight: '600', marginBottom: '6px', fontSize: '14px' }}>{sub.subject_name}</p>
                  <p style={{ fontSize: '13px', color: '#888', marginBottom: '10px' }}>
                    {sub.level_name} {/ม|มัธยม|junior|middle|senior|high/i.test(sub.level_code || sub.level_name || '') ? 'ม.' : 'ป.'}{sub.grade}{sub.classroom_room ? `/${sub.classroom_room}` : ''} | {sub.credits} หน่วยกิต
                  </p>
                  {sub.teacher_names && <p style={{ fontSize: '12px', color: '#666', display:'flex', alignItems:'center', gap:4, marginBottom: '6px' }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    {sub.teacher_names}</p>}
                  {/* Progress + Quality */}
                  {sub.subject_type !== 'กิจกรรม' && (() => {
                    const total = Number(sub.student_count) || 0;
                    const sc1 = Number(sub.scored_sem1) || 0;
                    const sc2 = Number(sub.scored_sem2) || 0;
                    const pct1 = total > 0 ? Math.round(sc1 / total * 100) : 0;
                    const pct2 = total > 0 ? Math.round(sc2 / total * 100) : 0;
                    const hasSem1 = Number(sub.indicator_sem1) > 0;
                    const hasSem2 = Number(sub.indicator_sem2) > 0;
                    const dotColor = (pct) => pct === 100 ? '#43a047' : pct > 0 ? '#ff9800' : '#e0e0e0';
                    const subProg = qualProgress[sub.id] || {};
                    const hasQual = qTypes.length > 0 && total > 0;
                    if (!hasSem1 && !hasSem2 && !hasQual) return null;
                    return (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '6px', padding: '6px 8px', background: '#f8f9fa', borderRadius: '8px', fontSize: '11px' }}>
                        {/* Score progress */}
                        {(hasSem1 || hasSem2) && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 3 }} title="ความคืบหน้าคะแนน">
                            <span style={{ fontSize: 12, flexShrink: 0 }}>📝</span>
                            {hasSem1 && <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(pct1), border: '1px solid rgba(0,0,0,0.08)' }} title={`คะแนน ภาค 1: ${pct1}%`}/>}
                            {hasSem2 && <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(pct2), border: '1px solid rgba(0,0,0,0.08)' }} title={`คะแนน ภาค 2: ${pct2}%`}/>}
                            <span style={{ fontSize: 9, color: '#999', whiteSpace: 'nowrap' }}>
                              {hasSem1 && hasSem2 ? `${pct1}/${pct2}` : hasSem1 ? `${pct1}%` : `${pct2}%`}
                            </span>
                          </div>
                        )}
                        {/* Quality progress */}
                        {hasQual && qTypes.map(t => {
                          const tProg = subProg[t.type_key] || {};
                          const s1 = tProg[1] || 0;
                          const s2 = tProg[2] || 0;
                          const qp1 = total > 0 ? Math.round(s1 / total * 100) : 0;
                          const qp2 = total > 0 ? Math.round(s2 / total * 100) : 0;
                          return (
                            <div key={t.type_key} style={{ display: 'flex', alignItems: 'center', gap: 3 }} title={t.label}>
                              <span style={{ fontSize: 12, flexShrink: 0 }}>{t.icon || '📋'}</span>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(qp1), border: '1px solid rgba(0,0,0,0.08)' }} title={`${t.label} ภาค 1: ${qp1}%`}/>
                              <span style={{ display: 'inline-block', width: 9, height: 9, borderRadius: '50%', background: dotColor(qp2), border: '1px solid rgba(0,0,0,0.08)' }} title={`${t.label} ภาค 2: ${qp2}%`}/>
                              <span style={{ fontSize: 9, color: '#999', whiteSpace: 'nowrap' }}>{qp1}/{qp2}</span>
                            </div>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <div style={{ display: 'flex', gap: '6px', marginTop: '6px', flexWrap: 'wrap' }}>
                    {!/ม|มัธยม|junior|middle|senior|high/i.test(sub.level_code || sub.level_name || '') && (<>
                    {(sub.subject_name || '').includes('ชุมนุม') ? (
                    <button className="btn btn-outline btn-sm" style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate('/clubs')}>
                      จัดการชุมนุม
                    </button>
                    ) : (
                    <button className="btn btn-outline btn-sm" style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/indicators/${sub.id}/${sub.classroom_id || 0}`)}>
                      ตัวชี้วัด
                    </button>
                    )}
                    <button className="btn btn-primary btn-sm" style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/scores/${sub.id}/${sub.classroom_id || 0}`)}>
                      กรอกคะแนน
                    </button>
                    <button className="btn btn-success btn-sm" style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px' }}
                      onClick={() => navigate(`/subject-result/${sub.id}/${sub.classroom_id || 0}`)}>
                      ผลการเรียน
                    </button>
                    {isSem2 && <button className="btn btn-sm" style={{ flex: '1 1 auto', minWidth: 0, whiteSpace: 'nowrap', padding: '4px 8px', fontSize: '12px', background: '#7b1fa2', color: '#fff', border: 'none' }}
                      onClick={async () => {
                        try {
                          const token = localStorage.getItem('wadphol_token') || '';
                          const res = await fetch(
                            `${API_BASE}/scores/pp5_cover.php?subject_id=${sub.id}&classroom_id=${sub.classroom_id || 0}`,
                            { headers: { Authorization: `Bearer ${token}` } }
                          );
                          if (!res.ok) throw new Error('PDF error');
                          const blob = await res.blob();
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = `ปพ5_ปก_${sub.subject_code || sub.id}_${sub.level_code || ''}${sub.grade || ''}-${sub.room || ''}.pdf`;
                          a.click();
                          window.URL.revokeObjectURL(url);
                        } catch { toast.error('ดาวน์โหลดปก ปพ.5 ไม่สำเร็จ'); }
                      }}>
                      ปก ปพ.5
                    </button>}
                    </>)}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* My Classrooms (homeroom/teacher/academic) */}
      {!isAcademic() && (myClasses.length > 0) && (
        <div className="card">
          <h2 className="page-title" style={{ marginBottom: '16px' }}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{display:'inline',verticalAlign:'middle',marginRight:7,marginBottom:2}}>
              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
            </svg>
            ห้องเรียน
          </h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '14px' }}>
            {myClasses.map(cls => (
              <div key={cls.key || cls.id} style={{
                border: '1.5px solid #e8e8e8',
                borderRadius: '12px',
                padding: '16px',
                cursor: cls.id ? 'pointer' : 'default',
                opacity: cls.id ? 1 : 0.55,
              }}
                onMouseEnter={e => { if (cls.id) e.currentTarget.style.borderColor = '#4CAF50'; }}
                onMouseLeave={e => e.currentTarget.style.borderColor = '#e8e8e8'}
                onClick={() => cls.id && navigate(`/students/${cls.id}`)}
              >
                <div style={{ marginBottom: '8px' }}>
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#2e7d32" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/>
                  </svg>
                </div>
                <div style={{ fontWeight: '700', fontSize: '16px', color: '#2e7d32' }}>
                  {cls.label}
                </div>
                <div style={{ fontSize: '13px', color: '#888', marginTop: '4px' }}>
                  {cls.level_name || ''}
                </div>
                {cls.homeroom1_name && (
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '6px' }}>
                    ครู: {cls.homeroom1_name}
                  </div>
                )}
                <div style={{ marginTop: '10px' }}>
                  <span className="badge badge-success">{cls.id ? 'จัดการนักเรียน →' : 'ยังไม่มีข้อมูล'}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════════════════
          ADMIN / ACADEMIC — ตารางสอนทั้งโรงเรียนวันนี้
          ══════════════════════════════════════════════════════════ */}
      {isAcademic() && (() => {
        const DOW_NAMES = ['อาทิตย์','จันทร์','อังคาร','พุธ','พฤหัสบดี','ศุกร์','เสาร์'];
        const dayLabel = DOW_NAMES[schoolDayInfo.dow] || '-';
        const subIndex = new Map();
        (schoolDaySubstitutes || []).forEach(s => {
          subIndex.set(`${s.classroom_id}|${s.period_id}`, s);
        });
        // รวม rows ที่มี classroom_id + period_id เดียวกัน (เช่น ลูกเสือ+เนตรนารีที่บันทึกแยก row) → รวมชื่อครู
        const mergeMap = new Map();
        (schoolDaySchedules || []).filter(s => s.teacher_id || s.classroom_id).forEach(s => {
          const k = `${s.classroom_id}|${s.period_id}`;
          if (!mergeMap.has(k)) {
            mergeMap.set(k, { ...s, _teacherNames: s.teacher_name ? [s.teacher_name] : [] });
          } else {
            const existing = mergeMap.get(k);
            if (s.teacher_name && !existing._teacherNames.includes(s.teacher_name)) {
              existing._teacherNames.push(s.teacher_name);
              existing.teacher_name = existing._teacherNames.join(' / ');
            }
          }
        });
        let rows = [...mergeMap.values()];
        if (schoolDayFilterTeacher) {
          const rawFiltered = (schoolDaySchedules || []).filter(s => String(s.teacher_id) === String(schoolDayFilterTeacher));
          // สร้าง set ของ classroom+period ที่ครูนี้สอน แล้วดึง merged row
          const keys = new Set(rawFiltered.map(s => `${s.classroom_id}|${s.period_id}`));
          rows = rows.filter(r => keys.has(`${r.classroom_id}|${r.period_id}`));
        }
        (schoolDaySubstitutes || []).forEach(s => {
          const exists = rows.some(r => String(r.classroom_id) === String(s.classroom_id) && String(r.period_id) === String(s.period_id));
          // เมื่อ filter ครู → เพิ่มเฉพาะคาบที่ครูที่เลือกไปสอนแทนคนอื่น (ไม่ใช่คาบที่ตัวเองถูกแทน เพราะนั้นอยู่ใน rows แล้ว)
          if (schoolDayFilterTeacher) {
            const isSubTeacher = String(s.substitute_teacher_id) === String(schoolDayFilterTeacher);
            if (!exists && isSubTeacher) {
              rows.push({
                _fromSubstitute: true,
                classroom_id: s.classroom_id, period_id: s.period_id,
                period_number: s.period_number, period_name: s.period_name,
                start_time: s.start_time, end_time: s.end_time,
                subject_id: s.effective_subject_id || s.subject_id,
                subject_name: s.subject_name, subject_code: s.subject_code,
                teacher_id: s.substitute_teacher_id, teacher_name: s.substitute_teacher_name,
                level_code: s.level_code, level_name: s.level_name,
                grade: s.grade, room: s.room,
              });
            }
          } else if (!exists) {
            rows.push({
              _fromSubstitute: true,
              classroom_id: s.classroom_id, period_id: s.period_id,
              period_number: s.period_number, period_name: s.period_name,
              start_time: s.start_time, end_time: s.end_time,
              subject_id: s.effective_subject_id || s.subject_id,
              subject_name: s.subject_name, subject_code: s.subject_code,
              teacher_id: null, teacher_name: '(จัดสอนแทน)',
              level_code: s.level_code, level_name: s.level_name,
              grade: s.grade, room: s.room,
            });
          }
        });
        const byPeriod = new Map();
        rows.forEach(r => {
          const k = String(r.period_id);
          if (!byPeriod.has(k)) byPeriod.set(k, []);
          byPeriod.get(k).push(r);
        });
        const sortedPeriods = [...byPeriod.entries()].sort((a, b) =>
          Number(a[1][0]?.period_number ?? 999) - Number(b[1][0]?.period_number ?? 999)
        );
        const teacherOptions = [...new Map(
          (schoolDaySchedules || []).filter(s => s.teacher_id).map(s => [String(s.teacher_id), { id: s.teacher_id, name: s.teacher_name }])
        ).values()].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'th'));

        return (
          <div className="card" style={{ marginBottom: 20 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14, flexWrap: 'wrap', gap: 8 }}>
              <h2 className="page-title" style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span>📅 ตารางสอนวันนี้ทั้งโรงเรียน</span>
                <span style={{ fontSize: 13, fontWeight: 400, color: '#666' }}>วัน{dayLabel} {new Date(schoolDayInfo.date || Date.now()).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                {(schoolDaySubstitutes || []).length > 0 && (
                  <span style={{ fontSize: 12, background: '#fff3e0', color: '#e65100', borderRadius: 12, padding: '2px 10px', fontWeight: 600 }}>
                    🔄 มีการสอนแทน {schoolDaySubstitutes.length} คาบ
                  </span>
                )}
              </h2>
              <select value={schoolDayFilterTeacher} onChange={e => setSchoolDayFilterTeacher(e.target.value)}
                style={{ fontSize: 13, padding: '4px 8px', borderRadius: 6, border: '1px solid #ccc' }}>
                <option value="">— ครูทั้งหมด —</option>
                {teacherOptions.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
            {schoolDayInfo.isHoliday ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#888', background: '#fff3e0', borderRadius: 8 }}>
                🎉 วันหยุด{schoolDayInfo.holidayName ? ` — ${schoolDayInfo.holidayName}` : ''}
              </div>
            ) : sortedPeriods.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 24, color: '#888' }}>ไม่มีตารางสอนวันนี้</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f5f5f5' }}>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: 70 }}>คาบ</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: 110 }}>เวลา</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', width: 80 }}>ห้อง</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>วิชา</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left' }}>ครูผู้สอน</th>
                      <th style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'left', width: 200 }}>หมายเหตุ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedPeriods.map(([pid, items]) => {
                      const sorted = [...items].sort((a, b) =>
                        (a.level_code || '').localeCompare(b.level_code || '', 'th')
                        || Number(a.grade || 0) - Number(b.grade || 0)
                        || Number(a.room || 0) - Number(b.room || 0)
                      );
                      return sorted.map((r, idx) => {
                        const sub = subIndex.get(`${r.classroom_id}|${r.period_id}`);
                        const isCovered = !!sub;
                        const teacherDisplay = isCovered ? sub.substitute_teacher_name : (r.teacher_name || '-');
                        const subjectDisplay = isCovered ? (sub.subject_name || r.subject_name || '-') : (r.subject_name || r.note || '-');
                        return (
                          <tr key={`${pid}_${r.classroom_id}_${r.teacher_id || 'x'}_${idx}`} style={{ background: isCovered ? '#fff8e1' : '#fff' }}>
                            {idx === 0 && <td rowSpan={sorted.length} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 700, background: '#fafafa', verticalAlign: 'middle' }}>{r.period_name || `คาบ ${r.period_number}`}</td>}
                            {idx === 0 && <td rowSpan={sorted.length} style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', color: '#666', fontSize: 12, background: '#fafafa', verticalAlign: 'middle' }}>{r.start_time?.slice(0,5)}-{r.end_time?.slice(0,5)}</td>}
                            <td style={{ padding: '8px', border: '1px solid #ddd', textAlign: 'center', fontWeight: 600, color: '#1565c0' }}>{levelCodePrefix(r.level_code)}{r.grade}/{r.room}</td>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {r.subject_code && <span style={{ color: '#888', marginRight: 6 }}>{r.subject_code}</span>}
                              {subjectDisplay}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd' }}>
                              {isCovered ? (
                                <>
                                  <span style={{ color: '#e65100', fontWeight: 600 }}>🔄 {teacherDisplay}</span>
                                  {sub.original_teacher_name && <div style={{ fontSize: 11, color: '#999', textDecoration: 'line-through' }}>{sub.original_teacher_name}</div>}
                                </>
                              ) : <span>{teacherDisplay}</span>}
                            </td>
                            <td style={{ padding: '8px', border: '1px solid #ddd', fontSize: 12, color: '#666' }}>
                              {isCovered && <span style={{ background: '#fff3e0', color: '#e65100', padding: '2px 6px', borderRadius: 4, fontWeight: 600 }}>สอนแทน{sub.original_teacher_name ? ` ${sub.original_teacher_name}` : ''}</span>}
                              {sub?.note && <div style={{ marginTop: 2 }}>{sub.note}</div>}
                              {r.note && !isCovered && <span>{r.note}</span>}
                            </td>
                          </tr>
                        );
                      });
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
